export interface BudgetLineFragment<T> {
  key: string;
  value: T;
  description: string;
  note: string;
  height: number;
  continuation: boolean;
}

export interface BudgetPdfPage<T> {
  lines: BudgetLineFragment<T>[];
  showSummary: boolean;
  continuation: boolean;
}

export interface BudgetPaginationMeasurements {
  firstPageCapacity: number;
  continuationPageCapacity: number;
  summaryHeight: number;
  includeSummary?: boolean;
}

export interface BudgetSafeAreaMeasurement {
  headerBottom: number;
  footerTop: number;
  content: Array<{ top: number; bottom: number }>;
}

export function isBudgetContentWithinSafeArea(
  measurement: BudgetSafeAreaMeasurement,
  tolerance = 0.5
): boolean {
  if (!Number.isFinite(measurement.headerBottom)
    || !Number.isFinite(measurement.footerTop)
    || measurement.headerBottom > measurement.footerTop + tolerance) {
    return false;
  }

  return measurement.content.every(rect =>
    Number.isFinite(rect.top)
    && Number.isFinite(rect.bottom)
    && rect.top >= measurement.headerBottom - tolerance
    && rect.bottom <= measurement.footerTop + tolerance
  );
}

type MeasureFragment<T> = (value: T, description: string, note: string, continuation: boolean) => number;

export function paginateMeasuredBudgetLines<T>(
  fragments: BudgetLineFragment<T>[],
  measurements: BudgetPaginationMeasurements
): BudgetPdfPage<T>[] {
  validateMeasurements(measurements);
  const pages: BudgetPdfPage<T>[] = [];
  let lines: BudgetLineFragment<T>[] = [];
  let usedHeight = 0;

  for (const fragment of fragments) {
    let capacity = pages.length === 0
      ? measurements.firstPageCapacity
      : measurements.continuationPageCapacity;
    if (!Number.isFinite(fragment.height) || fragment.height <= 0) {
      throw new Error(`El fragmento ${fragment.key} no cabe en una página lógica.`);
    }

    if (lines.length > 0 && usedHeight + fragment.height > capacity) {
      pages.push({ lines, showSummary: false, continuation: pages.length > 0 });
      lines = [];
      usedHeight = 0;
      capacity = measurements.continuationPageCapacity;
    } else if (lines.length === 0 && pages.length === 0 && fragment.height > capacity) {
      pages.push({ lines: [], showSummary: false, continuation: false });
      capacity = measurements.continuationPageCapacity;
    }

    if (fragment.height > capacity) {
      throw new Error(`El fragmento ${fragment.key} no cabe en una página lógica.`);
    }

    lines.push(fragment);
    usedHeight += fragment.height;
  }

  if (lines.length > 0) {
    pages.push({ lines, showSummary: false, continuation: pages.length > 0 });
  }
  if (pages.length === 0) {
    pages.push({ lines: [], showSummary: false, continuation: false });
  }

  if (measurements.includeSummary === false) {
    return pages;
  }

  const lastPage = pages.at(-1)!;
  const lastCapacity = pages.length === 1
    ? measurements.firstPageCapacity
    : measurements.continuationPageCapacity;
  const lastUsedHeight = lastPage.lines.reduce((sum, item) => sum + item.height, 0);
  if (lastUsedHeight + measurements.summaryHeight <= lastCapacity) {
    lastPage.showSummary = true;
  } else {
    pages.push({ lines: [], showSummary: true, continuation: true });
  }

  return pages;
}

export function fragmentMeasuredBudgetLine<T>(
  value: T,
  description: string,
  note: string,
  pageCapacity: number,
  measure: MeasureFragment<T>,
  key = String(value)
): BudgetLineFragment<T>[] {
  const fullHeight = measure(value, description, note, false);
  if (fullHeight <= pageCapacity) {
    return [{ key, value, description, note, height: fullHeight, continuation: false }];
  }
  if (!description && !note) {
    throw new Error(`El contenido de la línea ${key} no puede fragmentarse sin pérdida.`);
  }

  const fragments: BudgetLineFragment<T>[] = [];
  const appendText = (text: string, field: 'description' | 'note') => {
    let remaining = text;
    while (remaining) {
      const continuation = fragments.length > 0;
      const chunk = findFittingPrefix(remaining, candidate => measure(
        value,
        field === 'description' ? candidate : '',
        field === 'note' ? candidate : '',
        continuation
      ), pageCapacity);
      if (!chunk) {
        throw new Error(`El contenido de la línea ${key} no puede fragmentarse sin pérdida.`);
      }
      const fragmentDescription = field === 'description' ? chunk : '';
      const fragmentNote = field === 'note' ? chunk : '';
      fragments.push({
        key: `${key}:${fragments.length}`,
        value,
        description: fragmentDescription,
        note: fragmentNote,
        height: measure(value, fragmentDescription, fragmentNote, continuation),
        continuation,
      });
      remaining = remaining.slice(chunk.length);
    }
  };

  appendText(description, 'description');
  appendText(note, 'note');
  return fragments;
}

function findFittingPrefix(text: string, measure: (candidate: string) => number, capacity: number): string {
  const graphemes = segmentGraphemes(text);
  let low = 1;
  let high = graphemes.length;
  let best = 0;
  while (low <= high) {
    const middle = Math.floor((low + high) / 2);
    if (measure(graphemes.slice(0, middle).join('')) <= capacity) {
      best = middle;
      low = middle + 1;
    } else {
      high = middle - 1;
    }
  }
  return graphemes.slice(0, best).join('');
}

type SegmenterConstructor = new (
  locale?: string | string[],
  options?: { granularity: 'grapheme' }
) => { segment(value: string): unknown };

function segmentGraphemes(text: string): string[] {
  const Segmenter = (Intl as unknown as { Segmenter?: SegmenterConstructor }).Segmenter;
  if (Segmenter) {
    const segments = new Segmenter(undefined, { granularity: 'grapheme' }).segment(text);
    return Array.from(segments as Iterable<{ segment: string }>, item => item.segment);
  }

  return segmentGraphemesConservatively(text);
}

function segmentGraphemesConservatively(text: string): string[] {
  const clusters: string[] = [];
  let current = '';

  for (const codePoint of Array.from(text)) {
    if (!current) {
      current = codePoint;
      continue;
    }

    if (isGraphemeExtension(codePoint) || codePoint === '\u200D' || current.endsWith('\u200D')) {
      current += codePoint;
      continue;
    }

    if (isRegionalIndicator(codePoint) && isSingleRegionalIndicator(current)) {
      current += codePoint;
      continue;
    }

    clusters.push(current);
    current = codePoint;
  }

  if (current) clusters.push(current);
  return clusters;
}

function isGraphemeExtension(value: string): boolean {
  const codePoint = value.codePointAt(0) ?? 0;
  return /\p{Mark}/u.test(value)
    || (codePoint >= 0xFE00 && codePoint <= 0xFE0F)
    || (codePoint >= 0x1F3FB && codePoint <= 0x1F3FF);
}

function isRegionalIndicator(value: string): boolean {
  const codePoint = value.codePointAt(0) ?? 0;
  return codePoint >= 0x1F1E6 && codePoint <= 0x1F1FF;
}

function isSingleRegionalIndicator(value: string): boolean {
  return Array.from(value).length === 1 && isRegionalIndicator(value);
}

function validateMeasurements(measurements: BudgetPaginationMeasurements): void {
  const capacities = [measurements.firstPageCapacity, measurements.continuationPageCapacity];
  if (capacities.some(value => !Number.isFinite(value) || value <= 0)
    || !Number.isFinite(measurements.summaryHeight)
    || measurements.summaryHeight < 0
    || (measurements.includeSummary !== false && measurements.summaryHeight === 0)) {
    throw new Error('Las medidas de paginación deben ser positivas y finitas.');
  }
}
